import { RootState } from 'store';
import { ActionType } from '@common/enums/command-hub/command-buttons';
import { SharedState } from '@common/store/SharedProvider';
import { defaultRestSet } from '@common/interfaces/api/rest-set';
import { selectIsUserAdminOrHigher } from 'store/general/general-slice-selectors';
import { RobotStatusState } from '@common/interfaces/robot/status/robot-state';
import UserRole from 'models/enums/user-roles';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import TorizonUpdateStatus from 'models/enums/torizon-update-status';

const mapStateToShared = (state: RootState): SharedState => {
  const robotStatus = state.robots.robots.currentRobot?.status;
  const sharedRobotStatus: RobotStatusState | null = robotStatus
    ? { ...robotStatus, isUpdating: robotStatus.torizonUpdateStatus === TorizonUpdateStatus.IN_PROGRESS }
    : null;

  return {
    rooms: {
      floors: state.localRobot.floors,
      rooms: state.localRobot.rooms,
      missions: state.localRobot.missions,
      cleaningZones: state.localRobot.cleaningZones,
      teachRepeatPaths: state.localRobot.teachRepeatPaths,
      redZones: state.localRobot.redZones,
      limitZones: state.localRobot.limitZones,
      ruleZones: state.localRobot.ruleZones,
      landmarks: state.localRobot.landmarks,
      waypoints: state.localRobot.waypoints,
      gates: state.localRobot.gates,
      zonePlanner: state.localRobot.zonePlanner,
      schedules: state.localRobot.schedules,
      teaching: {
        currentTeaching: {
          floorId: null,
          roomId: null,
          zoneId: null,
          zoneName: null,
          currentStep: null,
        },
      },
    },
    status: {
      isCleaningActionEnabled:
        state.robots.robots.currentRobotData.actionsList?.actionList.find(
          (as) => as.action === ActionType.Clean,
        )?.isEnabled ?? false,
      currentRobotState: {
        status: sharedRobotStatus,
        position: state.robots.robots.currentRobotData?.position ?? null,
        coverageMap: state.robots.robots.currentRobotData?.coverageMap ?? null,
        mission: state.robots.robots.currentRobotData?.missionStatus ?? null,
        scheduling: state.robots.robots.currentRobotData?.schedulingStatus ?? null,
        cleaningData: null,
      },
    },
    appConfigurations: {
      isWebApp: false,
      isReadOnly:
        (state.general.customUserRole ?? state.general.user.data.role) === UserRole.MEMBER,
      isAdvancedModeEnabled: state.general.isAdvancedModeEnabled,
      webAppHasUnsyncedChanges:
        state.robots.robots.currentRobotData.localDataState?.webappHasUnsyncedChanges ?? false,
      cloudHasUnsyncedChanges: false,
      isAdminOrHigher: selectIsUserAdminOrHigher(state),
      distanceUnit: state.general.user.data.distanceUnit,
    },
    robotConfigurations: {
      isRobotConnected: state.robots.robots.currentRobot?.status?.active ?? false,
      robotApiConfiguration: {
        data: null,
        rest: defaultRestSet,
      },
      systemSettings: {
        data: null,
        rest: defaultRestSet,
      },
      systemSerialData: {
        data: null,
        rest: defaultRestSet,
      },
      relayGateApiConfiguration: {
        data: [],
        // the following will need to be adapted if we ever want to plug these into sphere
        putStatus: { status: HttpFetchStatus.Failed, error: null },
        getStatus: { status: HttpFetchStatus.Failed, error: null },
      },
      paramList: state.localRobotConfiguration.paramList,
      cleaningConfiguration: state.localRobotConfiguration.cleaningConfiguration,
      excludedDates: state.localRobotConfiguration.excludedDates,
      nav: state.localRobotConfiguration.nav,
      buttonConfiguration: state.localRobotConfiguration.buttonConfiguration,
      robotVersion: state.robots.robots.currentRobot?.model ?? null,
      robotSoftwareVersion: state.robots.robots.currentRobot?.softwareVersion ?? null,
    },
  };
};

export default mapStateToShared;
